/**
 * @module experience/monthly-charges
 * @copyright 1994-2017 T-Mobile US, Inc. All Rights Reserved.
 */

// Core Libraries
import { Component, OnInit, OnDestroy, Input } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
// interfaces
import { ConfigData } from '@experience/repdash/interfaces/aem-content.interface';
import { CustomerAccountBillingDetails } from '@support/api/proxies/responses/api-2006.interface';

// services
import { ObjectService } from '@support/state-object-hash-utilities/services/object.service';
import moment from 'moment';
import { LineLandingService } from '@experience/landing/common/services/line-landing.service';

// constants
import { SYMBOL_CONSTANTS } from '@support/shared/constants/device.constants';
import {
  LinesDetail,
  CustomerAccountLineBillingDetails,
  ServiceDetails,
} from '@support/api/proxies/responses/api-2040.interface';
import { CommonUtilService } from '@support/shared/services/common-util.service';
import { UserService } from '@support/shared/services/user.service';
/**
 * Contains information  on monthly charges on the account landing page
 */
@Component({
  selector: 'tmo-monthly-charges',
  templateUrl: './monthly-charges.component.html',
})
export class MonthlyChargesComponent implements OnInit, OnDestroy {
  /**
   * Stores values of Account Notification
   */
  @Input() public contentData: ConfigData;
  /**
   * Stores values of customer billing line details
   */
  public lineBillingDetails: LinesDetail;
  /**
   * Stores a boolean value
   */
  @Input() public showDescription: boolean;
  /**
   * Stores bill block details
   */
  @Input() public customerAccountBillingDetails: CustomerAccountBillingDetails;
  /**
   * Value to check entry point
   */
  @Input() public showOnLineDetails: boolean;
  /**
   * Stores values of current third party services
   */
  public thirdPartyServiceArray: ServiceDetails[];
  /**
   * Stores value of constant minus sign
   */
  public minus: string;
  /**
   * billBlock Details used to store billingCycle details
   */
  public billBlockDetails: CustomerAccountLineBillingDetails;
  /**
   * Unsubscriber for all subscribtions
   */
  private ngUnsubscribe: Subject<void>;
  constructor(
    private objectService: ObjectService,
    private lineLandingService: LineLandingService,
    protected userService: UserService,
    protected commonUtilService: CommonUtilService
  ) {
    this.showDescription = false;
    this.ngUnsubscribe = new Subject<void>();
  }

  /**
   * Loads the initial data of Account Landing Page
   * @returns void
   */
  public ngOnInit(): void {
    this.customerAccountBillingDetails.billCycleStartDate = moment(
      this.customerAccountBillingDetails.billCycleStartDate
    ).format('MM/DD/YYYY');
    this.customerAccountBillingDetails.billCycleEndDate = moment(
      this.customerAccountBillingDetails.billCycleEndDate
    ).format('MM/DD/YYYY');
    this.minus = SYMBOL_CONSTANTS.HYPHEN;
    this.getLineBillingDetails();
    this.lineLandingService.selectedMsidin$;
    this.lineLandingService.selectedMsidin$.pipe(takeUntil(this.ngUnsubscribe)).subscribe((billingDetails :CustomerAccountLineBillingDetails) => {
      this.getLineDetailsData(billingDetails);
    });
  }

  /**
   * Unsubscribes for all subscriptions
   * @returns void
   */
  public ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  /**
   * Get Billing Details for selected line
   * @return void
   */
  private getLineBillingDetails(): void {
    this.userService
      .getCustomerBillingDetailsData()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(
        (billingDetails: CustomerAccountLineBillingDetails) => {
          this.getLineDetailsData(billingDetails);
        },
        () => {
          this.lineLandingService.setErrorMessage(this.contentData.monthlyChargesError);
        }
      );
  }
  private getLineDetailsData(billingDetails: CustomerAccountLineBillingDetails): void {
    this.billBlockDetails = billingDetails;
    if (this.commonUtilService.isNotEmpty(billingDetails.lineDetails)) {
      this.lineBillingDetails = this.billBlockDetails.lineDetails;
      this.thirdPartyServiceArray = this.objectService.get(this.billBlockDetails,
        'lineDetails.services');
      if (this.thirdPartyServiceArray) {
        this.thirdPartyServiceArray = this.thirdPartyServiceArray.filter(
          (service: ServiceDetails) => service.thirdPartyService);
      }
    }
  }

}
